import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { PaperPlaneRight, Trash, UsersThree } from '@phosphor-icons/react';

import {
  getMessages,
  listParticipants,
  markRead,
  removeParticipant,
  sendMessage,
  type Conversation,
  type Message,
  type Participant,
} from '@/api/chat';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useConfirm } from '@/components/ConfirmDialog';
import { cn } from '@/lib/utils';

// initials shortens a display name into the 1-2 letter monogram used inside
// the message and participant avatars. Falls back to "?" when the name is
// missing (deleted user, no display name set) so the avatar stays a circle
// rather than collapsing to zero-width.
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  return (
    (parts[0]?.[0] ?? '').toUpperCase() +
    (parts[parts.length - 1]?.[0] ?? '').toUpperCase()
  );
}

// hueFromString picks a stable HSL hue from the sender's identity so the
// avatar of "Daniel" always looks the same wherever it renders, without
// having to ship a server-side avatar URL. Pastel-ish chroma/lightness to
// stay tonally adjacent to the design system without competing for attention.
function hueFromString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

function Avatar({
  name,
  seed,
  size = 'md',
}: {
  name: string;
  seed: string;
  size?: 'sm' | 'md';
}) {
  const hue = hueFromString(seed || name);
  const dim = size === 'sm' ? 'h-7 w-7 text-[0.625rem]' : 'h-9 w-9 text-xs';
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-heading font-semibold text-ink',
        dim,
      )}
      style={{ backgroundColor: `hsl(${hue} 70% 85%)` }}
    >
      {initials(name)}
    </span>
  );
}

// ChatThreadSheet is the right-side slide-over that shows one conversation's
// history and a composer. The conversation list stays the primary surface
// behind it (same pattern as MemberCard / TeamSheet). Realtime refresh is
// driven from the Chat page's socket subscription, which invalidates the
// shared ['chat','messages',slug] query key — this sheet just re-renders.

export interface ChatThreadSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  // The conversation to show. conversationId is authoritative (it comes from
  // the ?conversation= URL param, so deep links work even while the inbox
  // list is still loading or the row sits on another page); conversation is
  // the matching list row when available, used for member_count etc.
  conversationId: string | null;
  conversation: Conversation | null;
  // Resolved display title for the header (handles the direct/group fallback).
  title: string;
  // The caller's core.user id, used to align their own messages to the right.
  currentUserId: string | null;
  // True when the caller is the chat creator — gates the remove-member
  // controls inside the participants panel. Edits to name/members go through
  // the EditChatModal on the list page; the panel exposes the lightweight
  // "kick someone out" affordance in context.
  isOwner: boolean;
}

// formatTime renders a message timestamp as a 24h HH:MM today, or a medium
// date for older days — deterministic and locale-aware per the 24h-clock /
// ISO-date house rules (never AM/PM).
function formatTimestamp(iso: string, locale: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(d);
}

export function ChatThreadSheet({
  open,
  onOpenChange,
  slug,
  conversationId: conversationIdProp,
  conversation,
  title,
  currentUserId,
  isOwner,
}: ChatThreadSheetProps) {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const conversationId = conversationIdProp ?? conversation?.id ?? null;

  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const messagesQuery = useQuery({
    queryKey: ['chat', 'messages', slug, conversationId],
    queryFn: () => getMessages(slug, conversationId!, token),
    enabled: !!token && !!slug && !!conversationId && open,
  });

  const [participantsOpen, setParticipantsOpen] = useState(false);
  const participantsQuery = useQuery({
    queryKey: ['chat', 'participants', slug, conversationId],
    queryFn: () => listParticipants(slug, conversationId!, token),
    enabled: !!token && !!slug && !!conversationId && open && participantsOpen,
  });
  const participantCount = conversation?.member_count ?? 0;

  const removeMut = useMutation({
    mutationFn: (participantId: string) =>
      removeParticipant(slug, conversationId!, participantId, token),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['chat', 'participants', slug, conversationId],
      });
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', slug] });
    },
  });
  const onRemove = (participantId: string, label: string) =>
    void (async () => {
      const ok = await confirm({
        title: t('chat.removeMember.title'),
        body: t('chat.removeMember.body', { name: label }),
        confirmLabel: t('chat.removeMember.cta'),
        tone: 'destructive',
      });
      if (ok) removeMut.mutate(participantId);
    })();

  // Mark the thread read whenever it's opened, then refresh the inbox so the
  // unread badge clears. markRead is idempotent, so re-running on reopen is
  // harmless.
  useEffect(() => {
    if (!open || !conversationId || !token) return;
    void markRead(slug, conversationId, token).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', slug] });
      // Header chat badge (ChatHeaderButton) reads from ['chat', 'unread', slug].
      void queryClient.invalidateQueries({ queryKey: ['chat', 'unread', slug] });
    });
  }, [open, conversationId, slug, token, queryClient]);

  // The list comes back newest-first (DESC); render oldest-at-top so the
  // newest message sits at the bottom, where the composer is.
  const ordered = [...(messagesQuery.data ?? [])].reverse();

  // Keep the view pinned to the latest message as new ones arrive.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ordered.length, open]);

  const send = useMutation({
    mutationFn: () => sendMessage(slug, conversationId!, draft.trim(), token),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({
        queryKey: ['chat', 'messages', slug, conversationId],
      });
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', slug] });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (draft.trim() && conversationId) send.mutate();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
      >
        {/* The Sheet's absolute X close lives at right-3 top-3, so the people
            button sits at right-12 to clear it cleanly. The title block sits
            on its own; the button is absolute so its vertical position never
            drifts with the subtitle's presence. */}
        <SheetHeader className="relative border-b border-border p-4 pr-24">
          <SheetTitle className="truncate">{title}</SheetTitle>
          {participantCount > 0 && (
            <button
              type="button"
              onClick={() => setParticipantsOpen((v) => !v)}
              aria-expanded={participantsOpen}
              aria-label={t('chat.actions.viewParticipants')}
              className={cn(
                'absolute right-12 top-3 flex h-9 items-center gap-1.5 rounded-pill px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                participantsOpen
                  ? 'bg-muted text-foreground'
                  : 'text-foreground/80 hover:bg-muted hover:text-foreground',
              )}
            >
              <UsersThree className="h-4 w-4" weight="bold" aria-hidden />
              <span className="tabular-nums">{participantCount}</span>
            </button>
          )}
        </SheetHeader>

        {participantsOpen && (
          <div className="border-b border-border bg-muted/30 max-h-[45%] overflow-y-auto">
            <ParticipantsList
              query={participantsQuery}
              isOwner={isOwner}
              ownerUserId={conversation?.created_by_user_id ?? null}
              onRemove={onRemove}
            />
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
          {messagesQuery.isLoading && (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          )}
          {messagesQuery.isError && (
            <p role="alert" className="text-sm text-destructive">
              {t('chat.thread.loadError')}
            </p>
          )}
          {!messagesQuery.isLoading && !messagesQuery.isError && ordered.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('chat.thread.empty')}</p>
          )}
          <ul className="flex flex-col gap-3">
            {ordered.map((m: Message, i) => {
              const mine = !!currentUserId && m.sender_user_id === currentUserId;
              const prev = ordered[i - 1];
              // Group consecutive bubbles from the same sender — only the
              // first one in a run renders the avatar + name caption.
              const groupHead = !prev || prev.sender_user_id !== m.sender_user_id;
              const senderLabel = m.sender_display_name || t('chat.you');
              const seed = m.sender_user_id ?? m.sender_display_name ?? m.id;
              return (
                <li
                  key={m.id}
                  className={cn('flex gap-2', mine ? 'flex-row-reverse' : 'flex-row')}
                >
                  <div className="w-9 shrink-0">
                    {groupHead && !mine ? (
                      <Avatar name={senderLabel} seed={seed} />
                    ) : null}
                  </div>
                  <div
                    className={cn(
                      'flex max-w-[75%] flex-col',
                      mine ? 'items-end' : 'items-start',
                    )}
                  >
                    {groupHead && !mine && (
                      <span className="mb-0.5 px-1 text-[0.6875rem] font-semibold text-foreground/70">
                        {senderLabel}
                      </span>
                    )}
                    <div
                      className={cn(
                        'rounded-2xl px-3 py-2 text-sm',
                        mine
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground',
                      )}
                    >
                      {/* Plain text only — message bodies are UGC, never HTML. */}
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    </div>
                    <span className="mt-0.5 px-1 text-[0.6875rem] tabular-nums text-muted-foreground">
                      {formatTimestamp(m.created_at, i18n.language)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <form onSubmit={onSubmit} className="flex items-center gap-2 border-t border-border p-3">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('chat.composerPlaceholder')}
            aria-label={t('chat.composerPlaceholder')}
            maxLength={8000}
            className="h-10 flex-1 rounded-pill border border-border bg-background px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <button
            type="submit"
            disabled={!draft.trim() || send.isPending}
            aria-label={t('chat.send')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-primary text-primary-foreground transition-transform hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 motion-reduce:hover:scale-100"
          >
            <PaperPlaneRight className="h-5 w-5" weight="fill" aria-hidden />
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function ParticipantsList({
  query,
  isOwner,
  ownerUserId,
  onRemove,
}: {
  query: {
    data: Participant[] | undefined;
    isLoading: boolean;
    isError: boolean;
  };
  isOwner: boolean;
  ownerUserId: string | null;
  onRemove: (participantId: string, label: string) => void;
}) {
  const { t } = useTranslation('common');
  if (query.isLoading) {
    return <p className="p-4 text-sm text-muted-foreground">{t('chat.participants.loading')}</p>;
  }
  if (query.isError) {
    return (
      <p role="alert" className="p-4 text-sm text-destructive">
        {t('chat.participants.loadError')}
      </p>
    );
  }
  const list = query.data ?? [];
  if (list.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">{t('chat.participants.empty')}</p>;
  }
  return (
    <div className="p-2">
      <p className="px-2 pb-2 pt-1 font-heading text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">
        {t('chat.participants.title')}
      </p>
      <ul className="flex flex-col">
        {list.map((p) => {
          const label = p.display_name || p.email || '—';
          const seed = p.user_id ?? p.member_id ?? p.id;
          const isOwnerRow = !!p.user_id && p.user_id === ownerUserId;
          const canRemove = isOwner && !isOwnerRow;
          return (
            <li key={p.id} className="flex items-center gap-2 rounded-md px-2 py-1.5">
              <Avatar name={label} seed={seed} size="sm" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm text-foreground">{label}</span>
                {!p.has_account && (
                  <span className="truncate text-[0.6875rem] text-muted-foreground">
                    {t('chat.participants.pending')}
                  </span>
                )}
              </span>
              {canRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(p.id, label)}
                  aria-label={t('chat.removeMember.cta')}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash className="h-4 w-4" weight="bold" aria-hidden />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
